import React from 'react';
import SummaryCard from 'src/shared/components/card-templates/SummaryCard';
import ExploreButton from 'src/shared/components/explore-button/ExploreButton';
import IucnSpeciesChart from 'src/shared/components/iucn-species-chart/IucnSpeciesChart';
import InvasiveSpeciesIcon from 'src/shared/components/invasive-species-icon/InvasiveSpeciesIcon';
import IucnLogo from 'src/shared/components/iucn-logo';
import Loader from 'src/shared/components/loader/Loader';
import Alert from 'src/shared/components/ui/alert';
import usePermissions from 'src/shared/hooks/usePermissions';
import useNextPreview from 'src/shared/hooks/useNextPreview';
import SiteSpeciesRiskModal from 'src/app/portfolio/views/portfolio-site-explorer/components/site-species-risk-modal/SiteSpeciesRiskModal';
import { PortfolioSpeciesSourceProvider } from 'src/app/portfolio/state/portfolioSpeciesSource';
import { useSiteSpeciesCountsQuery } from '../state/api/siteSummaryGraphSlice';
import styles from '../SiteSummary.module.scss';

type Props = {
    siteId: string;
};

/**
 * The grid-backed species figures for one site, shown beside the projection-backed
 * section above so the two can be compared before the old one is retired (DD-5859).
 *
 * Internal only, and it enforces that itself rather than relying on its caller: an
 * external user must not see these figures, and must not have their page ask the grid
 * for them either — hence the skip, not just an early return.
 */
const SiteSpeciesRiskIndicatorsNext = ({ siteId }: Props) => {
    const { isCurrentUserInternal } = usePermissions();
    // Opted into with ?next=true and remembered for the session, so an internal user's
    // site summary is the customer's page until they ask to compare.
    const isPreviewEnabled = useNextPreview();
    const isShown = isCurrentUserInternal && isPreviewEnabled;
    const { currentData, isFetching } = useSiteSpeciesCountsQuery({ siteId }, { skip: !isShown || !siteId });
    const [modalTab, setModalTab] = React.useState<null | 'iucn' | 'invasives'>(null);

    if (!isShown) {
        return null;
    }

    // Recharts' datum key, not the grid's — IucnSpeciesChart is shared with the
    // projection-backed widget, which names the category differently.
    const chartData = (currentData?.iucn ?? []).map(entry => ({
        iucnRedListCategory: entry.category,
        count: entry.count,
    }));

    return (
        <div className='flex flex-col gap-3' data-testid='site-species-next'>
            <div className={`text-xl text-black ${styles.sectionTitle}`}>Species risk indicators (new)</div>
            <Alert severity='warning' data-testid='site-species-next-internal-only'>
                Internal only — not shown to customers. Species are read from the global prediction grid rather than the pipeline behind the
                section above, so the figures will differ.
            </Alert>
            {isFetching || !currentData ? (
                <div className='flex justify-center py-10'>
                    <Loader />
                </div>
            ) : (
                <>
                    <div className='flex gap-3 items-stretch'>
                        {/* The two cards carry the same title and Explore label as the
                            section above, so the testid is what tells them apart. */}
                        <div className='w-1/4' data-testid='site-species-next-invasive'>
                            <SummaryCard
                                containerClasses={`gap-3 p-5 h-full ${styles.invasiveSpeciesCard}`}
                                cardIcon={
                                    <div className={styles.iconGradient}>
                                        <InvasiveSpeciesIcon />
                                    </div>
                                }
                                cardTitle='Invasive Species'
                                cardSubtitle='Predicted invasive species overlap across site'
                                cardBody={
                                    <div className='flex flex-col gap-1'>
                                        <div className='text-[42px] text-grey-80'>{currentData.invasiveSpeciesCount}</div>
                                        <div className='text-sm text-grey-100'>Total invasive species</div>
                                    </div>
                                }
                                // No analytics event, unlike the section above: this page is
                                // internal, and firing the live event would count comparison
                                // clicks as customer use of it.
                                action={<ExploreButton sectionName='invasive-species' onClick={() => setModalTab('invasives')} />}
                            />
                        </div>

                        <div className='w-3/4' data-testid='site-species-next-red-list'>
                            <SummaryCard
                                containerClasses={`gap-3 p-5 h-full ${styles.iucnCard}`}
                                cardIcon={
                                    <div className={styles.iconGradient}>
                                        <IucnLogo />
                                    </div>
                                }
                                cardTitle='IUCN Red List species'
                                cardSubtitle='Predicted red list species overlap across site'
                                cardBody={
                                    <div className='flex gap-4'>
                                        <div className='flex flex-col'>
                                            <span className='text-[42px] text-grey-80'>{currentData.redListSpeciesCount}</span>
                                            <div className='text-sm text-grey-80'>Total red list species</div>
                                            {/* The narrower CR/EN/VU subset, which the projection-backed
                                                section does not distinguish from the Red List total. */}
                                            <div className='text-sm text-grey-100'>
                                                {currentData.threatenedSpeciesCount} threatened (CR, EN, VU)
                                            </div>
                                        </div>

                                        <div className='bg-grey-20 w-[1px]'>&nbsp;</div>

                                        <IucnSpeciesChart className='flex' chartData={chartData} />
                                    </div>
                                }
                                action={<ExploreButton sectionName='iucn-red-list-species' onClick={() => setModalTab('iucn')} />}
                            />
                        </div>
                    </div>

                    {/* The same modal the section above opens, pointed at the grid: the
                        counts are only worth comparing if the species behind them are. */}
                    {modalTab && (
                        <PortfolioSpeciesSourceProvider source='grid'>
                            <SiteSpeciesRiskModal siteId={siteId} initialTab={modalTab} onClose={() => setModalTab(null)} />
                        </PortfolioSpeciesSourceProvider>
                    )}
                </>
            )}
        </div>
    );
};

export default SiteSpeciesRiskIndicatorsNext;
